<template>
  <div class="position-relative">
    <div ref="mapContainer" :style="{ height: mapHeight, width: '100%' }" />

    <button
      v-if="interactive && showGeolocateControl && map"
      class="btn btn-sm btn-light reset-location-btn"
      title="Reset pin to current location"
      @click="resetToCurrentLocation"
    >
      <i class="fas fa-location-arrow"></i>
      <span class="d-none d-sm-inline"> Set Current Location</span>
    </button>
    <loading-overlay v-if="isLoading" style="top: 0;" />
  </div>
</template>

<script>
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import LoadingOverlay from '@/components/shared/LoadingOverlay'
import ToastNotifications from '@/components/shared/mixins/ToastNotifications'

export default {
  name: 'MapboxMap',
  components: {
    LoadingOverlay
  },
  props: {
    mapHeight: {
      type: String,
      default: '400px'
    },
    initialCenter: {
      type: Array,
      default: () => [-106.3468, 56.1304]
    },
    initialZoom: {
      type: Number,
      default: 3
    },
    interactive: {
      type: Boolean,
      default: true
    },
    showGeolocateControl: {
      type: Boolean,
      default: false
    },
    selectedCustomerCoord: {
      type: Object,
      default: () => {
        return null
      }
    },
    initialCoordinates: {
      type: Object,
      default: null // { lat: Number, lng: Number }
    }
  },
  mixins: [ToastNotifications],
  data () {
    return {
      map: null,
      marker: null,
      isLoading: false,
      mapboxAccessToken: process.env.VUE_APP_MAPBOX_ACCESS_TOKEN,
      geolocateControlInstance: null,
      lastKnownGPSLocation: null,
      defaultZoom: 11.5
    }
  },
  watch: {
    selectedCustomerCoord: {
      immediate: true,
      handler (newCoord) {
        if (newCoord && this.map) {
          const lngLat = [newCoord.long, newCoord.lat]
          this.map.flyTo({ center: lngLat, zoom: this.defaultZoom })
          this.updateMarker(lngLat)
        }
      }
    },
    initialCoordinates: {
      handler (newCoord) {
        if (newCoord && newCoord.lat && newCoord.lng && this.map) {
          const lngLat = [newCoord.lng, newCoord.lat]
          this.map.flyTo({ center: lngLat, zoom: this.defaultZoom })
          this.updateMarker({ lng: newCoord.lng, lat: newCoord.lat })
        }
      }
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.initializeMap()
    })
  },
  beforeDestroy () {
    this.destroyMap()
  },
  methods: {
    initializeMap () {
      if (this.map || !this.$refs.mapContainer) return // Prevent re-initialization

      this.isLoading = true
      mapboxgl.accessToken = this.mapboxAccessToken

      try {
        this.map = new mapboxgl.Map({
          container: this.$refs.mapContainer,
          style: 'mapbox://styles/mapbox/streets-v11',
          center: this.initialCenter,
          zoom: this.initialZoom
        })

        this.map.addControl(new mapboxgl.NavigationControl())

        let geolocate
        if (this.showGeolocateControl) {
          geolocate = new mapboxgl.GeolocateControl({
            positionOptions: { enableHighAccuracy: true },
            trackUserLocation: true
          })
          this.geolocateControlInstance = geolocate

          this.map.addControl(this.geolocateControlInstance)

          // Listen for the geolocate event from the control
          if (this.geolocateControlInstance) {
            this.geolocateControlInstance.on('geolocate', (e) => {
              const lngLat = {
                lng: e.coords.longitude,
                lat: e.coords.latitude
              }
              this.lastKnownGPSLocation = lngLat

              if (this.interactive) {
                this.map.flyTo({ center: [lngLat.lng, lngLat.lat], zoom: this.defaultZoom })
                this.updateMarker(lngLat)
              }
            })
          }
        }

        this.map.on('load', () => {
          this.isLoading = false
          this.$emit('map-loaded', this.map)

          this.map.resize()

          // Priority: 1. initialCoordinates, 2. selectedCustomerCoord, 3. geolocate
          if (this.initialCoordinates && this.initialCoordinates.lat && this.initialCoordinates.lng) {
            const initialLngLat = [this.initialCoordinates.lng, this.initialCoordinates.lat];
            this.map.flyTo({ center: initialLngLat, zoom: this.defaultZoom });
            this.updateMarker({ lng: this.initialCoordinates.lng, lat: this.initialCoordinates.lat });
          } else if (this.selectedCustomerCoord && this.selectedCustomerCoord.lat && this.selectedCustomerCoord.long) {
            const selectedLngLat = [this.selectedCustomerCoord.long, this.selectedCustomerCoord.lat]
            this.map.flyTo({ center: selectedLngLat, zoom: this.defaultZoom })
            this.updateMarker(selectedLngLat)
          } else if (this.showGeolocateControl && this.geolocateControlInstance && !this.selectedCustomerCoord) {
            this.geolocateControlInstance.trigger()
          }

          if (this.interactive) {
            this.map.on('click', (e) => {
              this.map.flyTo({ center: e.lngLat, zoom: this.defaultZoom })
              this.updateMarker(e.lngLat)
            })
          }
        })
      } catch (error) {
        this.quickDangerToast('Map', 'Error initializing map')
      }
    },

    updateMarker (lngLat) {
      if (!this.map) return
      const coordinates = {
        long: lngLat.lng,
        lat: lngLat.lat
      }

      if (this.marker) {
        this.marker.setLngLat(lngLat)
      } else {
        this.marker = new mapboxgl.Marker()
          .setLngLat(lngLat)
          .addTo(this.map)
      }

      this.$emit('location-selected', coordinates)
    },

    destroyMap () {
      if (this.map) {
        this.map.remove()
        this.map = null
        this.marker = null
      }
    },

    resetToCurrentLocation () {
      if (this.lastKnownGPSLocation) {
        const lngLat = this.lastKnownGPSLocation
        this.map.flyTo({ center: [lngLat.lng, lngLat.lat], zoom: this.defaultZoom })
        this.updateMarker(lngLat)
      } else if (this.geolocateControlInstance) {
        this.geolocateControlInstance.trigger()
      } else {
        this.quickWarningToast('Location', 'Geolocation control not available.')
      }
    }
  }
}
</script>

<style scoped>
.reset-location-btn {
  position: absolute;
  top: 10px;
  right: 45px;
  z-index: 1;
}
</style>
